import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { initKeycloak, setupTokenRefresh } from '../lib/keycloak';
import { getMe } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import { useClusterStore } from '../stores/clusterStore';
import LoginPage from '../pages/LoginPage';

interface Props {
  children: ReactNode;
}

export function KeycloakProvider({ children }: Props) {
  const [initialized, setInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const initStartedRef = useRef(false);
  const setUser = useAuthStore(s => s.setUser);
  const setReady = useAuthStore(s => s.setReady);

  useEffect(() => {
    if (initStartedRef.current) return;
    initStartedRef.current = true;

    (async () => {
      try {
        const authenticated = await initKeycloak();
        if (!authenticated) return;
        setupTokenRefresh();
        const user = await getMe();
        setUser(user);
        setReady(true);
        await useClusterStore.getState().fetchClusters();
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setInitialized(true);
      }
    })();
  }, [setUser, setReady]);

  if (!initialized) {
    return <LoginPage />;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-100">
        <div className="max-w-md text-center space-y-3 px-6">
          <h1 className="text-xl font-semibold tracking-tight">
            Falha ao autenticar
          </h1>
          <p className="text-sm text-slate-400 break-words">{error}</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
